import { connect } from 'react-redux';

/* Styled Components */
import {
  PageFooter,
  PageSubtitle,
  LegalText,
  SelectInputWrapper,
} from 'styles/signup/MultiScreenSignupPage';

/* Child Components */
import Button, { BUTTON_COLOR_KEYS } from 'components/common/Button';
import SectionHeader from 'components/signup/multiscreen/SectionHeader';
import WishSelectInput from 'components/common/WishSelectInput';
import WishTextInput from 'components/common/WishTextInput';

/* Actions */
import {
  setStoreCategory as setStoreCategoryAction,
  setReferralCode as setReferralCodeAction,
  submitSignupStep as submitSignupStepAction,
  setCurrentSignupStep as setCurrentSignupStepAction,
  setErrorMessage as setErrorMessageAction,
  clearErrorMessage as clearErrorAction,
} from 'app/signupSlice';

/* Selectors */
import {
  getFirstName,
  getLastName,
  getEmail,
  getPassword,
  getStoreType,
  getStoreName,
  getStoreAddressLine1,
  getStoreAddressLine2,
  getCity,
  getState,
  getZipCode,
  getCountry,
  getCountryCode,
  getPhoneNumber,
  getAutocompleteAttributions,
  getPlaceDetailsResult,
  isStoreNameValidated,
  getStoreHours,
  getStoreCategory,
  getReferralCode,
  getErrorMessage,
} from 'app/signupSlice';
import { getShowExperiment, getDeciderKeyResult } from 'app/experimentSlice';

/* Utils */
import { i18n } from 'utils/i18n';
import { getAPIStoreHours } from 'utils/StoreHours';
import { fbCompleteRegistration } from 'utils/Facebook';
import {
  sendGAEvent,
  EVENT_ACTION,
  BLUE_SIGNUP_ID,
  BLUE_RETAIL_SIGNUP_ID,
  GA_GROUP_NAME,
} from 'utils/GoogleAnalytics';

/* Constants */
import {
  SIGNUP_SCREEN,
  STORE_TYPE,
  CS_STORE_TYPE,
} from 'constants/SignupConstants';
import {
  getStoreTypes,
  getStoreCategories,
} from 'constants/SignupFormConstants';
import {
  SHOW_REFERRAL_FIELD,
  USE_CS_STORE_TYPE,
} from 'constants/ExperimentConstants';

/* Types */
import type { AppState, AppDispatch } from 'app/store';
import type { OptionsType as SubmitSignupStepOptionsType } from 'services/SubmitSignupStepService';

const mapStateToProps = (state: AppState) => ({
  firstName: getFirstName(state),
  lastName: getLastName(state),
  email: getEmail(state),
  password: getPassword(state),
  storeType: getStoreType(state),
  storeName: getStoreName(state),
  storeAddressLine1: getStoreAddressLine1(state),
  storeAddressLine2: getStoreAddressLine2(state),
  city: getCity(state),
  state: getState(state),
  zipCode: getZipCode(state),
  country: getCountry(state),
  countryCode: getCountryCode(state),
  phoneNumber: getPhoneNumber(state),
  autocompleteAttributions: getAutocompleteAttributions(state),
  placeDetailsResult: getPlaceDetailsResult(state),
  storeNameValidated: isStoreNameValidated(state),
  storeCategory: getStoreCategory(state),
  storeHours: getStoreHours(state),
  referralCode: getReferralCode(state),
  errorMessage: getErrorMessage(state),
  shouldShowReferralField: getShowExperiment(state, SHOW_REFERRAL_FIELD),
  useCSStoreType: getDeciderKeyResult(state, USE_CS_STORE_TYPE),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setStoreCategory: (storeCategory: number) =>
    dispatch(setStoreCategoryAction(storeCategory)),
  setReferralCode: (referralCode: string) =>
    dispatch(setReferralCodeAction(referralCode)),
  submitSignupStep: (args: {
    signupStep: Values<typeof SIGNUP_SCREEN>;
    nextSignupStep: Values<typeof SIGNUP_SCREEN>;
    options: SubmitSignupStepOptionsType;
  }) => dispatch(submitSignupStepAction(args)),
  setCurrentSignupStep: (signupStep: Values<typeof SIGNUP_SCREEN>) =>
    dispatch(setCurrentSignupStepAction(signupStep)),
  setErrorMessage: (errorMessage: string) =>
    dispatch(setErrorMessageAction(errorMessage)),
  clearError: () => dispatch(clearErrorAction()),
});

type PropsType = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const StoreCategoryPage = ({
  firstName,
  lastName,
  email,
  password,
  storeType,
  storeAddressLine1,
  storeAddressLine2,
  city,
  state,
  zipCode,
  country,
  countryCode,
  phoneNumber,
  storeName,
  autocompleteAttributions,
  placeDetailsResult,
  storeNameValidated,
  storeHours,
  storeCategory,
  setStoreCategory,
  referralCode,
  setReferralCode,
  submitSignupStep,
  setCurrentSignupStep,
  errorMessage,
  setErrorMessage,
  clearError,
  shouldShowReferralField,
  useCSStoreType,
}: PropsType) => {
  const onChangeStoreCategory = (selectedIndex: number) => {
    clearError();
    setStoreCategory(selectedIndex);
  };

  const onInputChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    clearError();
    const { value } = event.currentTarget;
    const { id } = event.currentTarget.dataset;
    switch (id) {
      case 'referralCode':
        setReferralCode(value);
        break;
      default:
        break;
    }
  };

  const storeCategories = getStoreCategories();
  const storeTypes = getStoreTypes();

  const isFormComplete = storeCategory != null;

  const onSubmit = () => {
    if (!isFormComplete) {
      setErrorMessage('Please select a category');
      return;
    }
    if (
      storeCategory !== null &&
      storeCategory !== undefined &&
      storeType !== null &&
      storeType !== undefined
    ) {
      // For V2 of the multi-screen signup flow, we will maintain state in
      // mongo so we don't need to resend the other fields on the final
      // screen as we do now.
      fbCompleteRegistration(
        city,
        zipCode,
        state,
        country,
        storeTypes[storeType],
        storeCategories[storeCategory],
        storeType,
        storeCategory,
      );
      sendGAEvent(BLUE_SIGNUP_ID, EVENT_ACTION.CONVERSION);
      sendGAEvent(GA_GROUP_NAME, EVENT_ACTION.SIGNUP, { method: 'email' });
      if (storeType === STORE_TYPE.RETAIL) {
        sendGAEvent(BLUE_RETAIL_SIGNUP_ID, EVENT_ACTION.CONVERSION);
      }
      // TODO: After launching the dkey, come back to refactor logic
      // to use a single store type enum using the CS numbering.
      let csStoreType;
      if (useCSStoreType) {
        csStoreType =
          storeType === STORE_TYPE.RETAIL
            ? CS_STORE_TYPE.RETAIL
            : CS_STORE_TYPE.HOME;
      }
      submitSignupStep({
        signupStep: SIGNUP_SCREEN.STORE_CATEGORY,
        nextSignupStep: SIGNUP_SCREEN.SUCCESS,
        options: {
          firstName,
          lastName,
          email,
          password,
          storeType,
          csStoreType,
          storeName,
          storeAddressLine1,
          storeAddressLine2,
          city,
          state,
          zipCode,
          country,
          countryCode,
          phoneNumber,
          placeDetailsResult,
          autocompleteAttributions,
          storeNameValidated,
          hoursList: getAPIStoreHours(storeHours),
          storeCategory,
          referralCode,
        },
      });
    }
  };
  const showError = Boolean(errorMessage);

  return (
    <>
      {shouldShowReferralField ? (
        <>
          <SectionHeader
            title={i18n('Last couple details')}
            onBackClicked={() => {
              clearError();
              setCurrentSignupStep(SIGNUP_SCREEN.STORE_HOURS);
            }}
          />
        </>
      ) : (
        <>
          <SectionHeader
            title={i18n('Select a store category')}
            onBackClicked={() => {
              clearError();
              setCurrentSignupStep(SIGNUP_SCREEN.STORE_HOURS);
            }}
          />
          <PageSubtitle center>
            {i18n('Choose the one that best describes your store')}
          </PageSubtitle>
        </>
      )}

      <SelectInputWrapper>
        <WishSelectInput
          title={shouldShowReferralField ? i18n('Store category') : ''}
          placeholder={i18n('Select a category')}
          items={storeCategories}
          onChange={onChangeStoreCategory}
          selectedIndex={storeCategory}
          styles={{ width: '100%', margin: '0 0 8px' }}
          dropdownItemStyle={{
            justifyContent: 'left',
            padding: '0 12px',
          }}
          isError={showError}
        />
      </SelectInputWrapper>

      <LegalText>
        {i18n(
          'This is just for our records and won’t affect your eligibility for Wish Local',
        )}
      </LegalText>
      {shouldShowReferralField && (
        <WishTextInput
          value={referralCode}
          title={i18n('Referral code (optional)')}
          stretch
          margin="16px 0 0 0"
          data-id="referralCode"
          onChange={onInputChange}
        />
      )}

      <PageFooter>
        <Button
          onClick={onSubmit}
          color={BUTTON_COLOR_KEYS.DARK_COBALT}
          stretch
          bold
          overrideStyle={{
            margin: '24px 0',
          }}
        >
          {i18n('Submit')}
        </Button>
      </PageFooter>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(StoreCategoryPage);
